// ---------------------------------------------------------------------------
// V23 — PAY.
//
// `05-build-roadmap.md` §V23, and `13-pay-and-the-upstream-data-contract.md` §3: "A worker asking 'why is this
// £112.86?' must get a line-by-line answer."
//
// ---------------------------------------------------------------------------
// THE BREAKDOWN IS THE SCREEN, AND THE TOTAL IS A CONSEQUENCE
//
// The obvious design is a table of totals with the working hidden behind a link nobody
// clicks. That is the wrong way round: the total is the number people argue about and the
// breakdown is the only thing that ends the argument. So a row expands in place, the lines
// are in `rule_order` because that is what composition means, and the running total is
// shown beside each one.
//
// NOTHING ON THIS SCREEN IS COMPUTED HERE. Every figure comes from `app.compute_pay`;
// `system/pay.ts` formats and labels and does no arithmetic. A gross derived in a browser
// from a rate and some hours is the second engine `13-…` §5 warns about.
//
// ---------------------------------------------------------------------------
// AND THE TRONC IS ON THIS SCREEN AND IS NOT IN ANY FIGURE ON IT
//
// V23c. `13-…` §4.6: a hospitality distribution "must not pass through the pay engine …
// never merged into gross". The tempting reading of that is to keep it off this screen
// entirely. That is the wrong way round for the same reason the breakdown is: the person
// who might merge the two is the person reading them, and a tronc they cannot see is one
// they will assume is inside the gross. So it is here, in its own panel, under a sentence
// that says it is not pay — and it is a SEPARATE FETCH from a SEPARATE MODULE, so nothing
// on the page holds both figures in one object where somebody could add them.
//
// ---------------------------------------------------------------------------
// AND IT SAYS WHEN NOTHING MOVED
//
// D10's boundary. The only enabled provider is a sandbox, and a batch that went to one
// looks exactly like a batch that went to a bank. Somebody who believes their crew has
// been paid when nothing moved finds out from the crew, so the screen says so in words on
// every batch it draws.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { may, send } from "../api/auth";
import { console_ } from "../api/console";
import { expenses as expensesApi, type ExpenseTotal } from "../api/expenses";
import { tronc as troncApi, type TroncDistribution } from "../api/tronc";
import { LoadFailed } from "../design/LoadFailed";
import { StatusChip } from "../design/StatusChip";
import { Tabs } from "../design/Tabs";
import { toneOf } from "../design/tone";
import { translateLabel } from "../i18n/translate";
import { useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import type { Failure } from "../system/failure";
import { RecordTronc } from "./TroncForms";
import {
  batchState,
  duration,
  money,
  payState,
  railNote,
  type BatchRow,
  type BreakdownLine,
  type PayRow,
} from "../system/pay";

type Calculation = {
  calculation_id: string;
  basis: string;
  currency: string;
  gross_minor: string;
  rounding_mode: string;
  is_superseded: boolean;
  breakdown: BreakdownLine[];
};

export function Pay() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const [rows, setRows] = useState<PayRow[] | null>(null);
  const [batches, setBatches] = useState<BatchRow[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<Calculation[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  // The read itself, apart from an action's refusal: a limit is calm, a fault can be tried again.
  const [loadFailed, setLoadFailed] = useState<Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const [distributions, setDistributions] = useState<TroncDistribution[] | null>(null);
  const [troncFailed, setTroncFailed] = useState<string | null>(null);
  const [troncRecorded, setTroncRecorded] = useState({});
  // Sweep §4 / 0190. Expenses are not pay either; their own read, their own failure.
  const [expenseTotals, setExpenseTotals] = useState<ExpenseTotal[] | null>(null);
  // S36. The rail a batch goes to is the deployment's first enabled provider, read
  // from the server rather than named in this file.
  const [provider, setProvider] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await send<{ pay?: PayRow[]; error?: { message: string } }>(
      console_.eventPay(apiBase, id),
    );
    if (res.status === 200 && res.body?.pay) {
      setRows(res.body.pay);
      setLoadFailed(null);
    } else {
      setLoadFailed({ status: res.status, refusal: refusal(res.body?.error) });
    }
    const b = await send<{ batches?: BatchRow[] }>(console_.paymentBatches(apiBase));
    if (b.status === 200 && b.body?.batches) setBatches(b.body.batches);
    const p = await send<{ providers?: { id: string; is_enabled: boolean }[] }>(console_.paymentProviders(apiBase));
    setProvider(p.body?.providers?.find((x) => x.is_enabled)?.id ?? null);
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  // ★ ITS OWN EFFECT, NOT A THIRD REQUEST INSIDE `load`. A tronc is not pay and
  // nothing on this screen may make it depend on pay having been read: a
  // distribution exists whether or not a shift has been priced, and folding the
  // fetch into `load` would make an unpriced event look like an event with no
  // tronc. Failing separately is the same argument — a tronc that could not be
  // read must say so rather than disappearing under a pay error.
  useEffect(() => {
    void (async () => {
      const res = await send<{ distributions?: TroncDistribution[]; error?: { message: string } }>(
        troncApi.forEvent(apiBase, id),
      );
      if (res.status === 200 && Array.isArray(res.body?.distributions)) {
        setDistributions(res.body.distributions);
        setTroncFailed(null);
      } else {
        setDistributions(null);
        setTroncFailed(refusal(res.body?.error) ?? t("The tronc could not be read."));
      }
    })();
  }, [apiBase, id, troncRecorded]);

  useEffect(() => {
    void (async () => {
      try {
        const r = await send<{ totals?: ExpenseTotal[] }>(expensesApi.forEvent(apiBase, id));
        setExpenseTotals(r.status === 200 && Array.isArray(r.body?.totals) ? r.body.totals : null);
      } catch {
        setExpenseTotals(null);
      }
    })();
  }, [apiBase, id]);

  const expand = useCallback(
    async (timesheetId: string) => {
      if (open === timesheetId) {
        setOpen(null);
        setDetail(null);
        return;
      }
      setOpen(timesheetId);
      setDetail(null);
      const res = await send<{ calculations?: Calculation[]; error?: { message: string } }>(
        console_.timesheetPay(apiBase, timesheetId),
      );
      if (res.status === 200 && res.body?.calculations) {
        setDetail(res.body.calculations);
      } else {
        setFailed(refusal(res.body?.error) ?? t("That breakdown could not be read."));
      }
    },
    [apiBase, open],
  );

  const price = useCallback(
    async (timesheetId: string, basis: string) => {
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(
        console_.computePay(apiBase, timesheetId, basis),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That shift could not be priced."));
        return;
      }
      await load();
    },
    [apiBase, load],
  );

  const runBatch = useCallback(async () => {
    const unpaid = (rows ?? []).filter((r) => r.pay_calculation_id !== null);
    if (unpaid.length === 0 || provider === null) return;
    setBusy(true);
    const res = await send<{ error?: { message: string } }>(
      console_.runPaymentBatch(
        apiBase,
        provider,
        unpaid.map((r) => r.pay_calculation_id as string),
      ),
    );
    setBusy(false);
    if (res.status >= 400) {
      setFailed(refusal(res.body?.error) ?? t("That batch could not be run."));
      return;
    }
    await load();
  }, [apiBase, rows, provider]);

  if (rows === null && loadFailed !== null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Pay")}</h2>
        </div>
        <LoadFailed failure={loadFailed} fallback={t("These figures could not be read.")} onRetry={() => void load()} />
      </section>
    );
  }
  if (rows === null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Pay")}</h2>
        </div>
        <p className="muted">{t("Reading the priced hours for this event…")}</p>
      </section>
    );
  }

  return (
    <Tabs
      label={t("Pay")}
      tabs={[
        { id: "pay", label: t("Pay"), content: (<>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Pay")}</h2>
          <p className="muted">{t("What each person is owed for this event, and how the figure was reached.")}</p>
        </div>
        {failed ? <StatusChip tone="failed" label={failed} /> : null}
        {loadFailed ? <LoadFailed failure={loadFailed} fallback={t("These figures could not be read.")} onRetry={() => void load()} /> : null}

        {rows.length === 0 ? (
          <>
          <p className="muted">
            {t("No hours have been submitted for this event yet. A shift can be priced as soon as its timesheet exists — approval and pricing are separate questions.")}
          </p>
          {/* The next step as a button: hours come from the timesheets. */}
          <Link className="btn btn-secondary" to={`/console/events/${encodeURIComponent(id)}/timesheets`}>
            {t("Open the timesheets")}
          </Link>
          </>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Person")}</th>
                <th>{t("Post")}</th>
                <th>{t("Payable")}</th>
                <th>{t("State")}</th>
                <th className="numeric">{t("Pay")}</th>
                <th className="numeric">{t("Charge")}</th>
                <th className="numeric">{t("Margin")}</th>
                <th>{t("Breakdown")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = payState(r, t);
                return (
                  <tr key={r.timesheet_id}>
                    <td>{r.worker_name}</td>
                    <td>
                      {translateLabel(locale, r.role_name)}
                      <div className="muted">{r.zone ?? t("No zone")}</div>
                    </td>
                    <td>{duration(r.payable_minutes)}</td>
                    <td>
                      <StatusChip tone={s.tone} label={s.label} />
                    </td>
                    <td className="numeric">{money(r.pay_gross_minor, r.currency)}</td>
                    <td className="numeric">{money(r.charge_gross_minor, r.charge_currency ?? r.currency)}</td>
                    <td className="numeric">{money(r.margin_minor, r.currency)}</td>
                    <td>
                      {r.is_priced ? (
                        <span className="row">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => void expand(r.timesheet_id)}
                          >
                            {open === r.timesheet_id ? t("Hide the working") : t("Show the working")}
                          </button>
                          {/* SWEEP S10. THE CHARGE SIDE COULD NOT BE PRICED FROM ANY SCREEN, and
                              an invoice is raised from charge calculations only. */}
                          {r.charge_calculation_id === null ? (
                            <button
                              type="button"
                              className="btn"
                              disabled={busy}
                              onClick={() => void price(r.timesheet_id, "charge")}
                            >
                              {t("Price the charge")}
                            </button>
                          ) : null}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn"
                          disabled={busy}
                          onClick={() => void price(r.timesheet_id, "pay")}
                        >
                          {t("Price this shift")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      
        <details className="role-group">
          <summary>{t("How pay is worked out")}</summary>
          <p className="muted">
            {t("Every rule that applied is listed in the order it applied, because the order changes the answer. A correction is a new calculation; nothing here is edited.")}
          </p>
        </details>
      </section>
      {open !== null ? (
        <section className="panel">
          <div className="panel-head">
            <h2>{t("How that figure was reached")}</h2>
          </div>
          {detail === null ? (
            <p className="muted">{t("Reading the breakdown…")}</p>
          ) : detail.length === 0 ? (
            <p className="muted">{t("That shift has not been priced, so there is no working to show.")}</p>
          ) : (
            detail.map((c) => (
              <div key={c.calculation_id}>
                <p>
                  <StatusChip
                    tone={c.is_superseded ? "attention" : "live"}
                    label={
                      c.is_superseded
                        ? t("Superseded by a later calculation")
                        : c.basis === "charge"
                          ? t("Current — what the client is charged")
                          : t("Current — what the worker is paid")
                    }
                  />
                </p>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="numeric">{t("Order")}</th>
                      <th>{t("Rule")}</th>
                      <th className="numeric">{t("This rule")}</th>
                      <th className="numeric">{t("Running total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.breakdown.map((l) => (
                      <tr key={l.seq}>
                        <td className="numeric">{l.rule_order}</td>
                        <td>
                          {l.label}
                          <div className="muted">{l.band}</div>
                        </td>
                        <td className="numeric">{money(l.amount_minor, c.currency)}</td>
                        <td className="numeric">{money(l.running_minor, c.currency)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="numeric" />
                      <td>
                        <strong>{t("Total")}</strong>
                        <div className="muted">{t("Rounded {mode} at each line", { mode: c.rounding_mode })}</div>
                      </td>
                      <td className="numeric" />
                      <td className="numeric">
                        <strong>{money(c.gross_minor, c.currency)}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))
          )}
        </section>
      ) : null}

</>) },
        { id: "batches", label: t("Payment batches"), content: (<>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Payment batches")}</h2>
          <p className="muted">{t("A batch is immutable once run; a mistake is corrected by a new batch.")}</p>
        </div>
        <p>
          <button
            type="button"
            className="btn"
            disabled={busy || provider === null || rows.every((r) => r.pay_calculation_id === null)}
            title={provider === null ? t("No payment provider is enabled for this deployment.") : undefined}
            onClick={() => void runBatch()}
          >
            {busy ? t("Working…") : t("Run a batch for every priced shift")}
          </button>
        </p>
        {batches === null || batches.length === 0 ? (
          <p className="muted">
            {t("No payment batch has been run. Pricing a shift and paying for it are separate steps, so nothing is sent until somebody sends it.")}
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Reference")}</th>
                <th>{t("Rail")}</th>
                <th className="numeric">{t("Payments")}</th>
                <th className="numeric">{t("Total")}</th>
                <th>{t("State")}</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const s = batchState(b, t);
                const rail = railNote(b, t);
                return (
                  <tr key={b.id}>
                    <td>
                      <Link to={`/console/payment-batches/${b.id}`}>{b.reference}</Link>
                    </td>
                    <td>
                      {b.provider_name ?? b.provider}
                      {rail ? (
                        <div>
                          <StatusChip tone={rail.tone} label={rail.label} />
                        </div>
                      ) : null}
                    </td>
                    <td className="numeric">{b.line_count}</td>
                    <td className="numeric">{money(b.total_minor, b.currency)}</td>
                    <td>
                      <StatusChip tone={s.tone} label={s.label} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

</>) },
        { id: "tronc", label: t("Tips and service charge"), content: (<>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Tronc and service charge")}</h2>
          {/* ★ THE SENTENCE IS THE FEATURE. §4.6 exists because a distribution merged
              into gross is taxed as pay, and the person most likely to merge them is
              whoever is reading this page beside the pay table. Saying it in words
              rather than by layout: a panel further down a page is not a statement
              about tax treatment, and a reader who scrolls past the heading has read
              nothing. */}
          <p className="muted">
            {t("Not pay. A share of a service charge is decided by the troncmaster, taxed differently, and never enters the figures above — no rule in the pay engine can express one. Recorded here against the shift it belongs to, exactly as it was reported. A correction is a new record; nothing here is edited.")}
          </p>
        </div>

        {troncFailed !== null ? (
          <StatusChip tone="failed" label={troncFailed} />
        ) : distributions === null ? (
          <p className="muted">{t("Reading the distributions for this event…")}</p>
        ) : distributions.length === 0 ? (
          <p className="muted">
            {t("Nothing has been shared out against this event. That is the ordinary case outside hospitality — a tronc is recorded only where a venue collects a service charge and somebody independent of the agency divides it.")}
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t("Shift")}</th>
                <th scope="col">{t("Arrangement")}</th>
                <th scope="col">{t("Period")}</th>
                <th className="numeric" scope="col">{t("Share")}</th>
                <th scope="col">{t("Standing")}</th>
              </tr>
            </thead>
            <tbody>
              {distributions.map((d) => (
                <tr key={d.id}>
                  <td>
                    {translateLabel(locale, d.role_name)}
                    <br />
                    <span className="muted">{d.worker_ref}</span>
                  </td>
                  <td>
                    {d.scheme_reference}
                    <br />
                    {/* ★ NEVER COLOUR ALONE, and the word here carries the whole legal
                        point: several jurisdictions require the troncmaster to be
                        independent of the employer, and an arrangement that is not is a
                        different tax position. The tone is `attention` rather than
                        `failed` because this product does not know which jurisdiction
                        applies — it reports the claim the agency made. */}
                    {d.is_independent_of_employer ? (
                      <StatusChip tone="neutral" label={t("Independent troncmaster")} />
                    ) : (
                      <StatusChip tone="attention" label={t("Troncmaster not independent")} />
                    )}
                    <br />
                    <span className="muted">
                      {t("{reference} — as declared by this agency, checked by nothing here", {
                        reference: d.troncmaster_reference,
                      })}
                    </span>
                  </td>
                  <td>
                    {t("{start} to {end}", { start: d.period_start, end: d.period_end })}
                  </td>
                  <td className="numeric">{money(d.amount_minor, d.currency)}</td>
                  <td>
                    <StatusChip
                      tone={toneOf(d.state_tone)}
                      label={d.state_label}
                    />
                    {d.is_superseded ? (
                      <>
                        <br />
                        <StatusChip tone="neutral" label={t("Corrected later")} />
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {me && may(me, "tronc.record") ? (
          <RecordTronc shifts={rows ?? []} onRecorded={() => setTroncRecorded({})} />
        ) : null}
      </section>

</>) },
        { id: "expenses", label: t("Expenses"), content: (<>
      <section className="panel stack">
        <h2>{t("Expenses")}</h2>
        <p className="muted">{t("Not pay. Reimbursed separately, taxed differently, never part of gross.")}</p>
        {expenseTotals === null ? (
          <p className="muted">{t("Not available to you.")}</p>
        ) : expenseTotals.length === 0 ? (
          <p className="muted">{t("No approved expenses on this event.")}</p>
        ) : (
          <ul className="plain-list">
            {expenseTotals.map((x) => (
              <li key={x.currency}>
                {t("{approved} approved · {recharged} recharged to the client · {waiting} waiting", {
                  approved: money(x.approved_minor, x.currency),
                  recharged: money(x.rechargeable_minor, x.currency),
                  waiting: x.waiting_count,
                })}
              </li>
            ))}
          </ul>
        )}
        <Link to={`/console/events/${id}/expenses`}>{t("Open the expense claims")}</Link>
      </section>
</>) },
      ]}
    />
  );
}
